import { HttpInterceptorFn, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { API_MESSAGE_SINK, BaseApiResponse, unwrapApiResponse } from '../../../@backoffice/models/api-response.model';
import { TimezoneService } from '../services/timezone.service';

/**
 * HTTP Interceptor that automatically unwraps BaseApiResponse<T> responses
 * 
 * Best Practices Implemented:
 * 1. Uses functional interceptor (HttpInterceptorFn) for modern Angular approach
 * 2. Leverages existing unwrapApiResponse() function for consistency
 * 3. Properly handles error responses (isSuccess: false) by throwing ApiResponseError
 * 4. Preserves blob responses (file downloads) unchanged
 * 5. Only processes responses that match BaseApiResponse structure
 * 
 * This interceptor:
 * - Automatically unwraps successful API responses (isSuccess: true)
 * - Throws ApiResponseError for failed API responses (isSuccess: false)
 * - Passes through non-API responses unchanged
 * - Skips blob responses (file downloads)
 * - Supports allowUndefinedData option via X-Allow-Undefined-Data header
 * 
 * Usage:
 * Services can now directly type their HTTP calls to the data type:
 *   this.http.get<Country[]>(endpoint)  // Instead of BaseApiResponse<Country[]>
 * 
 * The interceptor automatically:
 * 1. Detects BaseApiResponse structure
 * 2. Unwraps successful responses
 * 3. Throws ApiResponseError for failed responses (caught by service error handlers)
 */
export const responseUnwrapInterceptor: HttpInterceptorFn = (req, next) => {
  const timezoneService = inject(TimezoneService);

  return next(req).pipe(
    map((event: HttpEvent<any>) => {
      // Only process HttpResponse events
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      const body = event.body;

      // Skip unwrapping for blob responses (file downloads)
      if (body instanceof Blob) {
        return event;
      }

      // Skip unwrapping if body is null or undefined
      if (body === null || body === undefined) {
        return event;
      }

      // Check if the response matches BaseApiResponse structure
      // BaseApiResponse has: isSuccess, statusCode, message?, errorCode?, validationErrors?, data?
      const isBaseApiResponse = (
        typeof body === 'object' &&
        'isSuccess' in body &&
        'statusCode' in body &&
        typeof body.isSuccess === 'boolean' &&
        typeof body.statusCode === 'number'
      );

      // Only process if it matches BaseApiResponse structure
      if (!isBaseApiResponse) {
        return event;
      }

      // Capture the client DB server's UTC offset from the envelope before it is unwrapped away.
      // TimezoneService uses it to convert naive (client-local) timestamps to the viewer's time.
      if (typeof body.clientUtcOffsetMinutes === 'number') {
        timezoneService.setClientUtcOffsetMinutes(body.clientUtcOffsetMinutes);
      }

      // Hand the envelope message (often a translation key) to callers that asked for it.
      const messageSink = req.context.get(API_MESSAGE_SINK);
      if (messageSink && typeof body.message === 'string') {
        messageSink.message = body.message;
      }

      // Check if request has X-Allow-Undefined-Data header to support allowUndefinedData option
      const allowUndefinedData = req.headers.has('X-Allow-Undefined-Data');

      // Use the existing unwrapApiResponse function for consistency
      // This function will:
      // - Throw ApiResponseError if isSuccess is false
      // - Throw ApiResponseError if data is undefined (unless allowUndefinedData is true)
      // - Return the unwrapped data if successful
      try {
        const unwrapOptions = allowUndefinedData ? { allowUndefinedData: true } : undefined;
        const unwrappedData = unwrapApiResponse(body as BaseApiResponse<any>, unwrapOptions);
        
        // Create a new HttpResponse with the unwrapped data
        return event.clone({
          body: unwrappedData
        });
      } catch (error) {
        // unwrapApiResponse throws ApiResponseError for failed responses
        // Re-throw it so it can be caught by error handlers in services
        throw error;
      }
    })
  );
};

